import {
  ArrowTrendingDownIcon as ArrowTrendingDownSolidIcon,
  ArrowTrendingUpIcon as ArrowTrendingUpSolidIcon,
} from "@heroicons/react/24/solid";
import {
  ArrowTrendingDownIcon as ArrowTrendingDownOutlineIcon,
  ArrowTrendingUpIcon as ArrowTrendingUpOutlineIcon,
} from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";

interface PercentageIndicatorProps {
  valueNow: number;
  valueBefore: number;
  text?: string;
  className?: string;
  variant?: "default" | "banner";
  locale?: string;
}

export default function PercentageIndicator({
  valueNow,
  valueBefore,
  text,
  className = "",
  variant = "default",
  locale = "cs-CZ",
}: PercentageIndicatorProps) {
  const { percentage, isUp } = calculateChange(valueNow, valueBefore);

  function calculateChange(current: number, previous: number) {
    const difference = current - previous;

    const percentage = previous !== 0 ? (difference / previous) * 100 : 0;

    return {
      difference,
      percentage,
      isUp: difference >= 0,
    };
  }

  if (variant === "banner") {
    const BannerIcon = isUp
      ? ArrowTrendingUpOutlineIcon
      : ArrowTrendingDownOutlineIcon;

    return (
      <span
        className={cn(
          "inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium dark:bg-white/10",
          isUp
            ? "bg-softiq-secondary-shade-200 text-softiq-primary dark:text-softiq-secondary"
            : "bg-red-100 text-red-600 dark:text-red-400",
          className,
        )}
      >
        <BannerIcon aria-hidden="true" className="size-3 shrink-0" />
        {percentage >= 0 ? "+" : ""}
        {percentage.toLocaleString(locale, { maximumFractionDigits: 1 })} %
      </span>
    );
  }

  return (
    <span className={cn("app-pill-status", className)}>
      <span>{text}</span>

      <span
        className={`inline-flex items-center gap-x-1 text-xs font-semibold leading-none ${
          isUp ? "text-green-600" : "text-red-600"
        }`}
      >
        {isUp ? (
          <ArrowTrendingUpSolidIcon className="size-3 shrink-0" />
        ) : (
          <ArrowTrendingDownSolidIcon className="size-3 shrink-0" />
        )}
        {percentage >= 0 ? "+" : ""}
        {percentage.toFixed(1)} %
      </span>
    </span>
  );
}
